import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { FiLogOut, FiUsers, FiVolume2, FiVolumeX, FiX } from "react-icons/fi";
import { burstFrom, collapseTo } from "../../../lib/fx";
import { playSearchCancel, playSearchStart, setSfxEnabled, sfxEnabled } from "../../../lib/sfx";
import { levelProgress } from "../../../lib/authUser";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useElementWidth } from "../../../hooks/useElementWidth";
import ConfirmDialog from "../../../components/UI/ConfirmDialog";
import type { User } from "../../../types";
import { Num } from "./primitives";
import { EmptySlot, InviteSlot, OccupiedSlot, SlotMember } from "./PartySlot";
import ReadinessBar, { Cta, CtaModel, Reason } from "./ReadinessBar";

export type Member = SlotMember;
export type Party = {
  id: string;
  leaderId: string;
  members: Member[];
  queuedAt: string | null;
  matchId: string;
  invitations: { id: string; leader: string }[];
  debug?: boolean;
  debugEnabled?: boolean;
};

const SLOTS = 5;

const edge = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(400%); }
`;
const rise = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
`;
const heroIn = keyframes`
  from { opacity: 0; transform: scale(0.94); clip-path: inset(0 40% 0 40% round 16px); }
  to { opacity: 1; transform: scale(1); clip-path: inset(0 0 0 0 round 16px); }
`;
const heroOut = keyframes`
  from { opacity: 1; transform: scale(1); clip-path: inset(0 0 0 0 round 16px); }
  to { opacity: 0; transform: scale(0.94); clip-path: inset(0 40% 0 40% round 16px); }
`;
/* Кольцо не сжимаем сильнее 0.6 (тонкая рамка при этом рвётся) и плавно проявляем, чтобы не мигало. */
const ringOut = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  18% { opacity: 0.6; }
  100% { transform: scale(1.6); opacity: 0; }
`;
const glint = keyframes`
  from { transform: translateX(-120%) skewX(-18deg); }
  to { transform: translateX(380%) skewX(-18deg); }
`;

const Root = styled.section<{ $searching: boolean }>`
  position: relative;
  overflow: hidden;
  display: grid;
  gap: 24px;
  min-width: 0;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  box-shadow: ${(p) => (p.$searching ? "inset 0 0 0 1px rgba(255, 155, 87, 0.5)" : "inset 0 0 0 1px transparent")};
  transition: box-shadow 0.5s;
  [data-size="sm"] & {
    padding: 16px;
    gap: 20px;
  }
  /* Световые бегунки по верхней и нижней кромке: только transform, без перерисовки. */
  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 0;
    width: 25%;
    height: 2px;
    pointer-events: none;
    background: linear-gradient(90deg, transparent, #ffd2a8, transparent);
    opacity: ${(p) => (p.$searching ? 1 : 0)};
    transition: opacity 0.4s;
    animation: ${edge} 2.2s linear infinite;
  }
  &::before {
    top: 0;
  }
  &::after {
    bottom: 0;
    animation-direction: reverse;
  }
`;
const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 16px;
  h1 {
    font-size: 28px;
    line-height: 36px;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  .mode {
    font-size: 15px;
    line-height: 22px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  @media (max-width: 767px) {
    h1 {
      font-size: 24px;
      line-height: 32px;
    }
  }
`;
const Invite = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.primarySoft};
  animation: ${keyframes`from{opacity:0}to{opacity:1}`} 0.2s linear;
  .text {
    flex: 1 1 220px;
    min-width: 0;
    font-size: 15px;
    line-height: 22px;
    small {
      display: block;
      font-size: 13px;
      color: ${(p) => p.theme.colors.textSecondary};
    }
  }
  button {
    min-height: 44px;
    padding: 0 20px;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 650;
    background: ${(p) => p.theme.colors.primary};
    color: ${(p) => p.theme.colors.onPrimary};
    &:hover:not(:disabled) {
      background: ${(p) => p.theme.colors.primaryHover};
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;
const RosterHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  min-height: 44px;
  h2 {
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
    white-space: nowrap;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;
const Count = styled(Num)`
  font-size: 14px;
  font-weight: 500;
  color: ${(p) => p.theme.colors.textMuted};
`;
const Quiet = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  transition:
    background ${(p) => p.theme.motion.fast},
    color ${(p) => p.theme.motion.fast};
  svg {
    width: 16px;
    height: 16px;
  }
  .n {
    padding: 0 6px;
    border-radius: 6px;
    background: ${(p) => p.theme.colors.surfaceHover};
    font-size: 12px;
    line-height: 20px;
    font-variant-numeric: tabular-nums;
  }
  &:hover:not(:disabled) {
    background: ${(p) => p.theme.colors.raised};
    color: ${(p) => p.theme.colors.text};
  }
  &.leave:hover:not(:disabled) {
    background: ${(p) => p.theme.colors.dangerSoft};
    color: ${(p) => p.theme.colors.danger};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  [data-size="sm"] & .label {
    display: none;
  }
`;
const Grid = styled.ul<{ $searching?: boolean }>`
  list-style: none;
  > li {
    transition: box-shadow 0.5s;
    border-radius: 12px;
    box-shadow: ${(p) => (p.$searching ? "0 0 0 1px rgba(255, 155, 87, 0.45)" : "0 0 0 1px transparent")};
  }
  display: grid;
  grid-template-columns: repeat(${SLOTS}, minmax(0, 1fr));
  gap: 12px;
  [data-size="md"] & {
    gap: 8px;
  }
  [data-size="sm"] & {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }
`;
const Search = styled.div<{ $leaving: boolean }>`
  display: grid;
  gap: 10px;
  .hero {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px 28px;
    padding: 20px 24px;
    border-radius: 16px;
    background: linear-gradient(105deg, rgba(255, 122, 47, 0.22), rgba(255, 155, 87, 0.08) 55%, rgba(255, 122, 47, 0.18));
    box-shadow: inset 0 0 0 1px rgba(255, 155, 87, 0.35);
    animation: ${(p) => (p.$leaving ? heroOut : heroIn)} ${(p) => (p.$leaving ? "0.32s" : "0.55s")} cubic-bezier(0.16, 1, 0.3, 1) both;
    @media (max-width: 560px) {
      grid-template-columns: auto minmax(0, 1fr);
    }
    /* Блик пробегает по блоку поиска: transform, без перерисовки. */
    &::after {
      content: "";
      position: absolute;
      inset: 0 auto 0 0;
      width: 18%;
      pointer-events: none;
      background: linear-gradient(90deg, transparent, rgba(255, 210, 168, 0.18), transparent);
      animation: ${glint} 3.2s ease-in-out infinite;
    }
  }
  .radar {
    position: relative;
    width: 56px;
    height: 56px;
    flex: none;
    i {
      position: absolute;
      inset: 0;
      border-radius: 50%;
      border: 2px solid ${(p) => p.theme.colors.primary};
      opacity: 0;
      animation: ${ringOut} 2.1s ease-out infinite backwards;
    }
    i:nth-child(2) { animation-delay: 0.7s; }
    i:nth-child(3) { animation-delay: 1.4s; }
    b {
      position: absolute;
      inset: 20px;
      border-radius: 50%;
      background: ${(p) => p.theme.colors.primary};
    }
  }
  .clock {
    display: grid;
    min-width: 0;
  }
  .time {
    font-size: 48px;
    line-height: 52px;
    font-weight: 800;
    letter-spacing: -0.02em;
    /* Цифры одной ширины и без переноса: счётчик не «ездит». */
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    white-space: nowrap;
    color: ${(p) => p.theme.colors.primary};
  }
  .cta-slot {
    @media (max-width: 560px) {
      grid-column: 1 / -1;
      width: 100%;
    }
  }
  .foot {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
    animation: ${rise} 0.4s 0.3s both;
  }
`;
const SoundToggle = styled.button`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  color: ${(p) => p.theme.colors.textSecondary};
  transition: background ${(p) => p.theme.motion.fast}, color ${(p) => p.theme.motion.fast};
  svg { width: 18px; height: 18px; }
  &:hover { background: ${(p) => p.theme.colors.raised}; color: ${(p) => p.theme.colors.text}; }
`;
const Fade = styled.div`
  display: grid;
  gap: inherit;
  animation: ${rise} 0.4s both;
`;
const Sticky = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${(p) => p.theme.zIndex.sticky};
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: ${(p) => p.theme.colors.navigation};
  box-shadow: 0 -8px 24px #0008;
`;

export type StageProps = {
  user: User | null;
  party: Party | null;
  now: number;
  locked: boolean;
  busy: boolean;
  pendingAction: string;
  syncError: string;
  error: string;
  inviteBlocked: boolean;
  /** Показывать кнопку «Друзья» (панель друзей не помещается рядом). */
  showFriendsButton: boolean;
  friendsOnline: number | null;
  onAction: (body: { action: string; [key: string]: unknown }) => void;
  onInvite: () => void;
  onLinkSteam: () => void;
  onRetry: () => void;
  onOpenFriends: () => void;
};

export default function Stage(props: StageProps) {
  const { user, party, now, locked, busy, pendingAction, inviteBlocked } = props;
  const { t } = useLanguage();
  const [rootRef, width] = useElementWidth<HTMLDivElement>();
  const size = !width || width >= 720 ? "lg" : width >= 480 ? "md" : "sm";
  const [kick, setKick] = useState<Member | null>(null);
  const [debug, setDebug] = useState(false);
  const [sound, setSound] = useState(sfxEnabled);

  const loading = !party && !props.error;
  const members: Member[] =
    party?.members ??
    (user
      ? [{ id: user.id, name: user.username, avatar: user.avatar, steamId: user.steamId || "", elo: user.elo, ready: true }]
      : []);
  const isLeader = loading || party?.leaderId === user?.id;
  const me = members.find((m) => m.id === user?.id);
  const queued = !!party?.queuedAt;
  const failure = props.error || props.syncError;

  const noSteam = !user?.steamId;
  const withoutSteam = members.filter((m) => !m.steamId);
  const notReady = members.filter((m) => m.id !== user?.id && !m.ready);
  const canSearch = !locked && !noSteam && !!party && withoutSteam.length === 0 && notReady.length === 0;

  /* Препятствия по порядку важности: ближайшее показываем, остальные раскрываем. */
  const obstacles: string[] = [];
  if (isLeader && !noSteam) {
    withoutSteam.forEach((m) => obstacles.push(t("home.reason.member_steam", { name: m.name })));
    if (notReady.length) obstacles.push(t("home.reason.not_ready", { names: notReady.map((m) => m.name).join(", ") }));
  }

  let reason: Reason | null = null;
  let cta: CtaModel;
  if (failure) {
    reason = { tone: "error", text: failure };
    cta = { kind: "retry", label: t("common.retry"), onClick: props.onRetry };
  } else if (noSteam) {
    reason = { tone: "block", text: t("home.reason.steam") };
    cta = { kind: "link-steam", label: t("home.link_steam"), onClick: props.onLinkSteam };
  } else if (isLeader) {
    reason = obstacles.length
      ? { tone: "block", text: obstacles[0] }
      : loading
        ? null
        : { tone: "ok", text: t("home.ready_all") };
    cta = {
      kind: "search",
      label: t("home.find_match"),
      pendingLabel: t("home.search_starting"),
      pending: pendingAction === "search",
      disabled: !canSearch,
      onClick: (e) => {
        burstFrom(e.currentTarget);
        playSearchStart();
        props.onAction({ action: "search", debug: party?.debugEnabled && debug });
      },
    };
  } else {
    reason = { tone: "info", text: me?.ready ? t("home.reason.leader_only") : t("home.confirm_ready") };
    cta = {
      kind: "ready",
      label: me?.ready ? t("home.unready") : t("home.im_ready"),
      quiet: !!me?.ready,
      disabled: busy,
      onClick: () => props.onAction({ action: "ready", ready: !me?.ready }),
    };
  }
  const more = obstacles.slice(1);

  const searchCta: CtaModel | null =
    queued && isLeader
      ? {
          kind: "cancel",
          label: t("home.cancel_search"),
          pendingLabel: t("home.cancelling"),
          pending: pendingAction === "stop",
          quiet: true,
          disabled: locked,
          onClick: (e) => {
            collapseTo(e.currentTarget);
            playSearchCancel();
            props.onAction({ action: "stop" });
          },
        }
      : null;
  const activeCta = queued ? searchCta : cta;

  /* Поиск плавно сворачивается обратно, а не пропадает рывком. */
  const lastSearchCta = useRef<CtaModel | null>(null);
  if (searchCta) lastSearchCta.current = searchCta;
  const [heroShown, setHeroShown] = useState(queued);
  useEffect(() => {
    if (queued) {
      setHeroShown(true);
      return;
    }
    const id = window.setTimeout(() => setHeroShown(false), 380);
    return () => window.clearTimeout(id);
  }, [queued]);
  const leaving = heroShown && !queued;


  /* Закреплённая панель только на узком экране и только когда основное место действия вне viewport. */
  const ctaRef = useRef<HTMLDivElement>(null);
  const [offscreen, setOffscreen] = useState(false);
  const hasCta = !!activeCta;
  useEffect(() => {
    const el = ctaRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setOffscreen(!e.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [queued, hasCta]);
  const narrow = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 767px)").matches;
  const stuck = offscreen && narrow && !!activeCta;

  const seconds = party?.queuedAt ? Math.max(0, Math.floor((now - Date.parse(party.queuedAt)) / 1000)) : 0;
  const clock = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  const empty = Math.max(0, SLOTS - members.length);

  return (
    <Root ref={rootRef} data-size={size} $searching={queued} data-fx-host aria-labelledby="home-title">
      <Head>
        <h1 id="home-title">{t("common.play")}</h1>
        <span className="mode">{t("home.mode")}</span>
        <SoundToggle
          type="button"
          aria-pressed={sound}
          aria-label={sound ? t("home.sound_off") : t("home.sound_on")}
          title={sound ? t("home.sound_off") : t("home.sound_on")}
          onClick={() => {
            setSound(!sound);
            setSfxEnabled(!sound);
          }}
        >
          {sound ? <FiVolume2 aria-hidden="true" /> : <FiVolumeX aria-hidden="true" />}
        </SoundToggle>
      </Head>

      {party?.invitations.map((i) => (
        <Invite key={i.id} role="status">
          <div className="text">
            <small>{t("home.invitation")}</small>
            {t("home.invitation_text", { name: i.leader })}
          </div>
          <button type="button" disabled={locked} onClick={() => props.onAction({ action: "accept", partyId: i.id })}>
            {t("home.join")}
          </button>
        </Invite>
      ))}

      <div>
        <RosterHead>
          <h2>
            {t("home.team")}
            <Count>
              {members.length || 1} / {SLOTS}
            </Count>
          </h2>
          <div className="actions">
            {props.showFriendsButton && (
              <Quiet type="button" onClick={props.onOpenFriends} aria-label={t("home.friends_open")}>
                <FiUsers aria-hidden="true" />
                <span className="label">{t("home.friends_open")}</span>
                {props.friendsOnline !== null && props.friendsOnline > 0 && <span className="n">{props.friendsOnline}</span>}
              </Quiet>
            )}
            {members.length > 1 && !queued && (
              <Quiet
                type="button"
                className="leave"
                disabled={locked}
                aria-label={t("home.leave")}
                title={t("home.leave")}
                onClick={() => props.onAction({ action: "leave" })}
              >
                <FiLogOut aria-hidden="true" />
                <span className="label">{t("home.leave")}</span>
              </Quiet>
            )}
          </div>
        </RosterHead>
        <Grid $searching={queued} aria-label={t("home.team")}>
          {members.map((m) => {
            const lead = loading || m.id === party?.leaderId;
            return (
              <OccupiedSlot
                key={m.id}
                m={m}
                isSelf={m.id === user?.id}
                isLeaderSlot={lead}
                ready={m.ready}
                progress={levelProgress(m.elo ?? 0)}
                canManage={isLeader && !loading && m.id !== user?.id && !queued}
                locked={locked}
                onMakeLeader={() => props.onAction({ action: "leader", userId: m.id })}
                onKick={() => setKick(m)}
              />
            );
          })}
          {Array.from({ length: empty }, (_, i) =>
            i === 0 && !queued ? (
              <InviteSlot key="invite" disabled={inviteBlocked && !!party} onClick={props.onInvite} />
            ) : (
              <EmptySlot key={"open" + i} />
            ),
          )}
        </Grid>
      </div>

      {heroShown ? (
        <Search $leaving={leaving}>
          <div className="hero" role="status">
            <span className="radar" aria-hidden="true">
              <i />
              <i />
              <i />
              <b />
            </span>
            <div className="clock">
              <span className="label">{t("home.searching")}</span>
              <span className="time" role="timer" aria-live="off" aria-label={`${seconds} ${t("home.sec")}`}>
                {clock}
              </span>
            </div>
            {(searchCta || lastSearchCta.current) && (
              <div ref={ctaRef} className="cta-slot" aria-hidden={stuck || undefined} {...(stuck ? ({ inert: "" } as object) : {})}>
                <Cta cta={{ ...(searchCta || (lastSearchCta.current as CtaModel)), disabled: leaving || (searchCta || lastSearchCta.current)?.disabled }} />
              </div>
            )}
          </div>
          <p className="foot">{isLeader ? t("home.searching_hint") : t("home.searching_member")}</p>
        </Search>
      ) : (
        <Fade>
          <ReadinessBar reason={reason} more={more} cta={cta} loading={loading} ctaHidden={stuck} ctaRef={ctaRef} />
          {party?.debugEnabled && isLeader && (
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
              <input type="checkbox" checked={debug} disabled={locked} onChange={(e) => setDebug(e.target.checked)} />
              {t("home.debug_mode")}
            </label>
          )}
        </Fade>
      )}

      {stuck && activeCta && (
        <>
          <div style={{ height: 72 }} aria-hidden="true" />
          <Sticky>
            <Cta cta={activeCta} />
          </Sticky>
        </>
      )}

      {kick && (
        <ConfirmDialog
          title={t("home.kick_title", { name: kick.name })}
          description={t("home.kick_text")}
          confirmLabel={t("home.kick")}
          onClose={() => setKick(null)}
          onConfirm={() => {
            props.onAction({ action: "kick", userId: kick.id });
            setKick(null);
          }}
        />
      )}
    </Root>
  );
}
